import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  Plus, Wallet, HandCoins, Clock, CheckCircle, XCircle,
  CircleDollarSign,
} from "lucide-react";
import { useMyAdvances } from "../../hooks";

const statusStyles = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-green-50 text-green-700 border-green-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  completed: "bg-blue-50 text-blue-700 border-blue-200",
};

const MyAdvances = () => {
  const { currentBusiness } = useAuth();
  
  // Use custom hook for data fetching
  const { advances, loading, stats, applyAdvance } = useMyAdvances(currentBusiness?.id);
  
  // Local state for form
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ type: "advance", amount: "", reason: "", tenure_months: "" });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setCreating(true);
    try {
      await applyAdvance({ ...form, amount: parseFloat(form.amount) });
      toast.success("Request submitted! Your admin will review it.");
      setCreateOpen(false);
      setForm({ type: "advance", amount: "", reason: "", tenure_months: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit"); }
    finally { setCreating(false); }
  };

  const formatCurrency = (v) => `${parseFloat(v || 0).toLocaleString("en-IN")}`;
  const formatDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="my-advances-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">My Advances & Loans</h1>
          <p className="text-slate-500">Request and track salary advances or loans</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary gap-2" data-testid="my-create-advance-btn"><Plus className="h-4 w-4" /> New Request</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2"><Wallet className="h-5 w-5 text-primary" /> Request Advance / Loan</DialogTitle>
              <DialogDescription>Submit a request to your admin</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                    <SelectTrigger className="input-field" data-testid="my-advance-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="advance">Salary Advance</SelectItem>
                      <SelectItem value="loan">Loan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input type="number" min="1" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                    placeholder="50000" className="input-field" required data-testid="my-advance-amount-input" />
                </div>
              </div>
              {form.type === "loan" && (
                <div className="space-y-2">
                  <Label>Preferred Tenure (months)</Label>
                  <Input type="number" min="1" value={form.tenure_months} onChange={e => setForm({ ...form, tenure_months: e.target.value })}
                    placeholder="12" className="input-field" data-testid="my-advance-tenure-input" />
                </div>
              )}
              <div className="space-y-2">
                <Label>Reason</Label>
                <Textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}
                  placeholder="Why do you need this advance or loan?" className="input-field min-h-[80px]" data-testid="my-advance-reason-input" />
              </div>
              <Button type="submit" className="w-full btn-primary" disabled={creating} data-testid="my-submit-advance-btn">
                {creating ? "Submitting..." : "Submit Request"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards - using hook's stats */}
      {advances.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Card className="card-soft">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-xl bg-amber-100"><Clock className="h-5 w-5 text-amber-600" /></div>
              <div>
                <p className="text-xl font-bold text-slate-800">{stats.pending}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card className="card-soft">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-xl bg-green-100"><CheckCircle className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-xl font-bold text-slate-800">{stats.approved}</p>
                <p className="text-xs text-slate-500">Active</p>
              </div>
            </CardContent>
          </Card>
          <Card className="card-soft">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-xl bg-red-100"><CircleDollarSign className="h-5 w-5 text-red-600" /></div>
              <div>
                <p className="text-xl font-bold text-slate-800">{formatCurrency(stats.outstanding)}</p>
                <p className="text-xs text-slate-500">Outstanding</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Records List */}
      {advances.length === 0 ? (
        <Card className="card-soft">
          <CardContent className="py-16 text-center">
            <Wallet className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">No advance or loan requests yet</p>
            <p className="text-sm text-slate-400 mt-1">Click "New Request" to submit one</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {advances.map(rec => {
            const progress = rec.amount > 0 ? Math.min(100, ((rec.total_repaid || 0) / rec.amount) * 100) : 0;
            return (
              <Card key={rec.id} className="card-soft" data-testid={`my-advance-${rec.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline" className={rec.type === "loan" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-teal-50 text-teal-700 border-teal-200"}>
                          {rec.type === "loan" ? <HandCoins className="h-3 w-3 mr-1" /> : <Wallet className="h-3 w-3 mr-1" />}
                          {rec.type === "loan" ? "Loan" : "Advance"}
                        </Badge>
                        <Badge variant="outline" className={statusStyles[rec.status]}>
                          {rec.status === "pending" && <Clock className="h-3 w-3 mr-1" />}
                          {rec.status === "approved" && <CheckCircle className="h-3 w-3 mr-1" />}
                          {rec.status === "rejected" && <XCircle className="h-3 w-3 mr-1" />}
                          {rec.status === "completed" && <CircleDollarSign className="h-3 w-3 mr-1" />}
                          {rec.status.charAt(0).toUpperCase() + rec.status.slice(1)}
                        </Badge>
                      </div>
                      <p className="font-semibold text-slate-800 text-lg">{formatCurrency(rec.amount)}</p>
                      {rec.reason && <p className="text-sm text-slate-500 mt-1">{rec.reason}</p>}
                      {rec.status === "approved" && (
                        <div className="mt-3 space-y-1.5">
                          <div className="flex items-center justify-between text-xs text-slate-500">
                            <span>Repaid: {formatCurrency(rec.total_repaid)}</span>
                            <span>Remaining: {formatCurrency(rec.amount - (rec.total_repaid || 0))}</span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-2">
                            <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
                          </div>
                          {rec.emi_amount && <p className="text-xs text-slate-400">EMI: {formatCurrency(rec.emi_amount)}/month</p>}
                        </div>
                      )}
                      {rec.admin_remarks && <p className="text-xs text-slate-400 mt-2 italic">Admin: {rec.admin_remarks}</p>}
                    </div>
                    <span className="text-xs text-slate-300 whitespace-nowrap">{formatDate(rec.created_at)}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyAdvances;
